/**
 * Reusable Data Table Component
 * 
 * A flexible table component for displaying business data with:
 * - Customizable columns and data
 * - Built-in sorting and filtering
 * - Action buttons for each row
 * - Loading and empty states
 * - Responsive design
 * 
 * @author Wildlife Project Team
 * @version 1.0 - Initial implementation
 */

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Search, 
  Filter, 
  MoreHorizontal, 
  ChevronDown, 
  ChevronUp,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TableColumn<T> {
  key: string;
  header: string;
  accessor: (item: T) => string | number | React.ReactNode;
  sortable?: boolean;
  width?: string;
  className?: string;
}

export interface TableAction<T> {
  key: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  onClick: (item: T) => void;
  condition?: (item: T) => boolean;
  className?: string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  actions?: TableAction<T>[];
  title?: string;
  description?: string;
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchKeys?: string[];
  filterable?: boolean;
  filters?: {
    key: string;
    label: string;
    options: { value: string; label: string }[];
  }[];
  onFilterChange?: (filters: Record<string, string>) => void;
  emptyMessage?: string;
  emptyIcon?: React.ComponentType<{ className?: string }>;
  className?: string;
  rowClassName?: (item: T) => string;
  onRowClick?: (item: T) => void;
}

export function DataTable<T extends { id?: string | number; _id?: string }>({
  data,
  columns,
  actions = [],
  title,
  description,
  loading = false,
  error = null,
  onRefresh,
  searchable = true,
  searchPlaceholder = "Search...",
  searchKeys = [],
  filterable = false,
  filters = [],
  onFilterChange,
  emptyMessage = "No data found",
  emptyIcon: EmptyIcon = AlertTriangle,
  className = "",
  rowClassName,
  onRowClick
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = data;

    // Apply search filter
    if (searchTerm && searchKeys.length > 0) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        searchKeys.some(key => {
          const value = (item as any)[key];
          return value && String(value).toLowerCase().includes(term);
        })
      );
    }

    // Apply column filters
    if (Object.keys(activeFilters).length > 0) {
      console.log('Applying filters:', activeFilters);
      filtered = filtered.filter(item => {
        const result = Object.entries(activeFilters).every(([key, value]) => {
          if (!value) return true;
          
          // Get the raw data value from the item
          const rawValue = (item as any)[key];
          console.log(`Filtering ${key}: rawValue=${rawValue}, filterValue=${value}`);
          
          if (rawValue === undefined || rawValue === null) return false;
          
          // Handle boolean values (like isActive for status)
          if (typeof rawValue === 'boolean') {
            const stringValue = value.toLowerCase();
            const match = (stringValue === 'true' && rawValue === true) || (stringValue === 'false' && rawValue === false);
            console.log(`Boolean match: ${match}`);
            return match;
          }
          
          // Handle string values
          const match = String(rawValue).toLowerCase().includes(value.toLowerCase());
          console.log(`String match: ${match}`);
          return match;
        });
        console.log(`Item ${(item as any).name || 'unnamed'} result:`, result);
        return result;
      });
    }

    // Apply sorting
    if (sortConfig) {
      const column = columns.find(col => col.key === sortConfig.key);
      if (column && column.sortable) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = column.accessor(a);
          const bValue = column.accessor(b);
          
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            const comparison = aValue.localeCompare(bValue);
            return sortConfig.direction === 'asc' ? comparison : -comparison;
          }
          
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
          }
          
          return 0;
        });
      }
    }

    return filtered;
  }, [data, searchTerm, searchKeys, activeFilters, sortConfig, columns]);

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleFilterChange = (filterKey: string, value: string) => {
    console.log('Filter change:', filterKey, value);
    const newFilters = { ...activeFilters, [filterKey]: value };
    console.log('New filters:', newFilters);
    setActiveFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  const getSortIcon = (key: string) => {
    if (sortConfig?.key !== key) return null;
    return sortConfig.direction === 'asc' ? ChevronUp : ChevronDown;
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="pt-12 pb-12">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="pt-12 pb-12 text-center">
          <div className="text-red-600 mb-2">⚠️ {error}</div>
          {onRefresh && (
            <Button onClick={onRefresh} variant="outline" className="mt-2">
              Try again
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("bg-white shadow-sm border-gray-200", className)}>
      {/* Header */}
      {(title || description) && (
        <CardHeader className="bg-gray-50/50 border-b border-gray-100">
          <div>
            {title && <CardTitle className="text-gray-900">{title}</CardTitle>}
            {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
          </div>
        </CardHeader>
      )}

      <CardContent className="p-6">
        {/* Search and Filters */}
        {(searchable || filterable || onRefresh) && (
          <div className="flex items-center justify-between gap-4 mb-6">
            {/* Left side - Search */}
            <div className="flex-1 max-w-md">
              {searchable && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-10 bg-white border-gray-300 focus:border-primary focus:ring-primary"
                  />
                </div>
              )}
            </div>
            
            {/* Right side - Actions */}
            <div className="flex items-center gap-3">
              {filterable && filters.length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="default" className="h-10 border-gray-300 hover:border-gray-400">
                      <Filter className="w-4 h-4 mr-2" />
                      Filters
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-4 bg-white" align="end">
                    <div className="space-y-4">
                      {filters.map((filter) => (
                        <div key={filter.key} className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">{filter.label}</label>
                          <select
                            value={activeFilters[filter.key] || ''}
                            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          >
                            <option value="">All</option>
                            {filter.options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              
              {onRefresh && (
                <Button 
                  onClick={onRefresh} 
                  variant="outline" 
                  size="default" 
                  className="h-10 border-gray-300 hover:border-gray-400"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Table */}
        {processedData.length === 0 ? (
          <div className="text-center py-12">
            <EmptyIcon className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No data found</h3>
            <p className="text-gray-600">{emptyMessage}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/80 hover:bg-gray-50">
                  {columns.map((column) => (
                    <TableHead 
                      key={column.key}
                      className={cn(
                        "text-gray-700 font-semibold border-b border-gray-200",
                        column.className,
                        column.sortable && "cursor-pointer hover:bg-gray-100 transition-colors",
                        column.width && `w-[${column.width}]`
                      )}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                                              <div className="flex items-center gap-2">
                          {column.header}
                          {column.sortable && (() => {
                            const Icon = getSortIcon(column.key);
                            return Icon ? <Icon className="w-4 h-4" /> : null;
                          })()}
                        </div>
                    </TableHead>
                  ))}
                  {actions.length > 0 && (
                    <TableHead className="w-[100px]">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                <AnimatePresence>
                  {processedData.map((item, index) => (
                    <motion.tr
                      key={item.id || item._id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.05 }}
                      className={cn(
                        "hover:bg-gray-50/80 transition-colors border-b border-gray-100",
                        onRowClick && "cursor-pointer hover:bg-blue-50/50",
                        rowClassName?.(item)
                      )}
                      onClick={() => onRowClick?.(item)}
                    >
                      {columns.map((column) => (
                        <TableCell key={column.key} className={column.className}>
                          {column.accessor(item)}
                        </TableCell>
                      ))}
                      {actions.length > 0 && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                              {actions
                                .filter(action => !action.condition || action.condition(item))
                                .map((action) => {
                                  const Icon = action.icon;
                                  return (
                                    <DropdownMenuItem
                                      key={action.key}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        action.onClick(item);
                                      }}
                                      className={cn(
                                        "bg-white hover:bg-gray-50 transition-colors",
                                        action.variant === 'destructive' && "text-red-600 hover:bg-red-50",
                                        action.className
                                      )}
                                    >
                                      {Icon && <Icon className="w-4 h-4 mr-2" />}
                                      {action.label}
                                    </DropdownMenuItem>
                                  );
                                })}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
